import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import InlineAlert from '../../../../shared/components/InlineAlert';
import { MISFIT_LIMIT, useEventDatasetMisfits } from '../../hooks/useEventDatasets';
import { inputLabel } from '../../utils/datasetMappingUtils';
import type { EventDatasetMisfit } from '../../../../types/backend';

interface DatasetMisfitsListProps {
  customerId: string;
  datasetName: string;
  /** With more than one input, each event names the input it was read with. */
  inputCount: number;
  column: string;
  /** Misfits, kept out of the history, or untranslated values of rows that were kept; each is read on its own. */
  kind: EventDatasetMisfit['kind'];
  onClose: () => void;
}

const COPY = {
  misfit: {
    title: (column: string) => `Events that did not fit ${column}`,
    none: (column: string) => `The archive returned no events that did not fit ${column}.`,
    failed: 'Could not read the events that did not fit',
    last: 'Reason',
  },
  untranslated: {
    title: (column: string) => `Values in ${column} kept without a translation`,
    none: (column: string) => `The archive returned no values in ${column} kept without a translation.`,
    failed: 'Could not read the values kept without a translation',
    last: 'Value',
  },
} as const;

const DatasetMisfitsList: React.FC<DatasetMisfitsListProps> = ({ customerId, datasetName, inputCount, column, kind, onClose }) => {
  const { misfits, loading, error } = useEventDatasetMisfits(customerId, datasetName, column, kind);
  const severalInputs = inputCount > 1;
  const copy = COPY[kind];

  let body: React.ReactNode;
  if (error) {
    body = <InlineAlert severity="error">{copy.failed}: {error}</InlineAlert>;
  } else if (loading) {
    body = <CircularProgress size={24} aria-label="Reading events" />;
  } else if (misfits.length === 0) {
    body = <Typography variant="body2" color="text.secondary">{copy.none(column)}</Typography>;
  } else {
    body = (
      <Table size="small" aria-label={copy.title(column)}>
        <TableHead>
          <TableRow>
            {severalInputs && <TableCell>Input</TableCell>}
            <TableCell>Event time</TableCell>
            <TableCell>Event ID</TableCell>
            <TableCell>Row</TableCell>
            <TableCell>{copy.last}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {misfits.map((m) => (
            <TableRow key={`${m.event_id}-${m.row_position}-${m.reason}`}>
              {severalInputs && <TableCell>{inputLabel(m.input)}</TableCell>}
              <TableCell>{m.event_time}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>{m.event_id}</TableCell>
              <TableCell>{m.row_position}</TableCell>
              {kind === 'misfit'
                ? <TableCell>{m.reason}</TableCell>
                : <TableCell sx={{ fontFamily: 'monospace' }}>{m.value}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }} data-testid="dataset-misfits-list">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">{copy.title(column)}</Typography>
        <Button size="small" onClick={onClose}>Close</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Read live from the event archive, newest {MISFIT_LIMIT} at most. Nothing is stored: these events are read
        again each time you open this list.
        {kind === 'untranslated' && ' These rows are in the dataset; each value is kept as read, since no translation covers it.'}
      </Typography>
      {body}
    </Box>
  );
};

export default DatasetMisfitsList;
